// Visitor tracking client (frontend half of the Visitor Data module).
//
// What it does:
//   * identifies the guest via the persistent guest_id (localStorage + cookie),
//   * captures the visitor's country once per session via a free HTTPS IP
//     geolocation API (ipwho.is, geojs.io fallback — ip-api.com equivalents
//     that work on HTTPS pages),
//   * logs page_view / search / add_to_cart events to Supabase through the
//     SECURITY DEFINER RPCs from the visitor_tracking migration.
//
// Everything is fire-and-forget and failure-tolerant: tracking must NEVER
// break the storefront. No-ops entirely when Supabase isn't configured.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { ENV, isSupabaseConfigured } from '@/lib/constants';
import { getGuestId } from '@/lib/guestId';

export type TrackedEvent = 'page_view' | 'search' | 'add_to_cart';

// Lightweight dedicated client: no auth session at all (RPCs are granted to
// the anon role), so tracking can't interfere with the app or chat sessions.
const trackingClient: SupabaseClient | null = isSupabaseConfigured
  ? createClient(ENV.supabaseUrl, ENV.supabaseAnonKey, {
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    })
  : null;

const GEO_CACHE_KEY = 'fs-geo-cache';
let initialized = false;

interface GeoInfo {
  country: string;
  countryCode: string;
  city: string;
}

async function fetchJson(url: string, timeoutMs = 6000): Promise<Record<string, unknown> | null> {
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    const res = await fetch(url, { signal: ctl.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Country lookup with a session cache so we hit the geo API at most once. */
async function lookupGeo(): Promise<GeoInfo> {
  try {
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached) return JSON.parse(cached) as GeoInfo;
  } catch {
    /* ignore */
  }

  let geo: GeoInfo = { country: '', countryCode: '', city: '' };

  // Primary: ipwho.is (free, HTTPS, CORS-enabled).
  const who = await fetchJson('https://ipwho.is/?fields=success,country,country_code,city');
  if (who && who.success !== false && typeof who.country === 'string') {
    geo = {
      country: who.country,
      countryCode: typeof who.country_code === 'string' ? who.country_code : '',
      city: typeof who.city === 'string' ? who.city : '',
    };
  } else {
    // Fallback: geojs.io (also free/HTTPS/CORS).
    const gj = await fetchJson('https://get.geojs.io/v1/ip/geo.json');
    if (gj && typeof gj.country === 'string') {
      geo = {
        country: gj.country,
        countryCode: typeof gj.country_code === 'string' ? gj.country_code : '',
        city: typeof gj.city === 'string' ? gj.city : '',
      };
    }
  }

  try {
    if (geo.country) sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(geo));
  } catch {
    /* ignore */
  }
  return geo;
}

/** Append one event row, creating the visitor row server-side if needed. */
export function trackEvent(eventType: TrackedEvent, payload: Record<string, unknown> = {}): void {
  if (!trackingClient) return;
  const guest = getGuestId();
  if (!guest) return;
  void trackingClient
    .rpc('track_visitor_event', { p_guest: guest, p_event_type: eventType, p_payload: payload })
    .then(({ error }) => {
      if (error) console.warn('[tracking] event failed:', error.message);
    });
}

/** Log a search query, tied to the guest_id. Debounced by the caller hook. */
export function trackSearch(query: string): void {
  const clean = query.trim();
  if (clean.length < 2) return;
  trackEvent('search', { query: clean.slice(0, 200) });
}

/** Log an Add-to-Cart action with exactly what was selected. */
export function trackAddToCart(item: {
  id: string;
  title: string;
  variant?: string;
  priceCents?: number;
  quantity?: number;
}): void {
  trackEvent('add_to_cart', {
    id: String(item.id).slice(0, 120),
    title: String(item.title).slice(0, 200),
    variant: item.variant ? String(item.variant).slice(0, 120) : undefined,
    price_cents: item.priceCents,
    quantity: item.quantity ?? 1,
  });
}

/**
 * Initialise tracking once per page load: registers/refreshes the visitor row
 * (with geolocation) and logs the landing page_view. Idempotent.
 */
export function initTracking(): void {
  if (initialized || typeof window === 'undefined' || !trackingClient) return;
  initialized = true;

  const guest = getGuestId();
  if (!guest) return;

  void (async () => {
    const geo = await lookupGeo();
    const { error } = await trackingClient.rpc('track_visitor', {
      p_guest: guest,
      p_country: geo.country,
      p_country_code: geo.countryCode,
      p_city: geo.city,
      p_user_agent: navigator.userAgent ?? '',
    });
    if (error) console.warn('[tracking] visitor upsert failed:', error.message);
    trackEvent('page_view', {
      path: window.location.pathname,
      referrer: document.referrer ? document.referrer.slice(0, 300) : '',
    });
  })();
}
